import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

function ScoreBadge({ score }) {
    if (score == null) return <span style={{ color: "var(--text-muted)" }}>--</span>;
    const color = score >= 8 ? "#22c55e" : score >= 5 ? "#eab308" : "var(--red)";
    return <span style={{ color, fontWeight: 600 }}>{score}/10</span>;
}

function Reviews() {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState("non-reviewed");
    const [sortBy, setSortBy] = useState("newest");
    const [stats, setStats] = useState({ completed: 0, inProgress: 0, unreviewed: 0 });
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
    });
    const [toDate, setToDate] = useState("");

    const loadReviews = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (sortBy === "site") params.sortBy = "site";
            else if (sortBy === "state") params.sortBy = "state";
            else if (sortBy === "oldest") params.sortBy = "oldest";
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const data = await api.reviews(params);
            setReviews(data.reviews || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load stats independently of the reviews list so the count cards
    // populate on initial mount and aren't gated on the list query.
    const loadStats = async () => {
        try {
            const statsData = await api.reviewStats({
                from: fromDate || undefined,
                to: toDate || undefined,
            });
            const t = statsData?.totals || {};
            setStats({
                completed: t.completedReviews || 0,
                inProgress: t.inProgressReviews || 0,
                unreviewed: t.unreviewedScans || 0,
            });
        } catch (err) {
            console.error("Failed to load review stats:", err);
        }
    };

    useEffect(() => { loadReviews(); }, [statusFilter, sortBy, fromDate, toDate]); // eslint-disable-line
    useEffect(() => { loadStats(); }, [fromDate, toDate]); // eslint-disable-line

    const statusColor = (s) => {
        if (s === "completed") return "#22c55e";
        if (s === "in_progress") return "#3b82f6";
        return "var(--text-muted)";
    };

    const statusLabel = (s) => {
        if (s === "completed") return "Reviewed";
        if (s === "in_progress") return "Under Review";
        return "Not Reviewed";
    };

    const handleOpen = (r) => {
        if (r.type === "dip") {
            navigate(`/reviews/dip/${r.id}`);
        } else {
            navigate(`/reviews/${r.siteId}/${encodeURIComponent(r.scanHour)}`);
        }
    };

    if (loading) return <LoadingSpinner message="Loading reviews..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load reviews</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={loadReviews}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <h2>Scan Reviews</h2>
                <p>{reviews.length} scan{reviews.length !== 1 ? "s" : ""} matching filters</p>
            </div>

            {/* Stats row — clickable as filters */}
            <div className="stats-grid" style={{ marginBottom: 16 }}>
                {[
                    { key: "completed",    label: "Reviewed",     color: "#22c55e",           count: stats.completed  },
                    { key: "in_progress",  label: "Under Review", color: "#3b82f6",           count: stats.inProgress },
                    { key: "non-reviewed", label: "Not Reviewed", color: "var(--text-muted)", count: stats.unreviewed },
                ].map(s => (
                    <div
                        key={s.key}
                        className="stat-card"
                        style={{
                            cursor: "pointer",
                            outline: statusFilter === s.key ? `2px solid ${s.color}` : "none",
                            outlineOffset: -2,
                        }}
                        onClick={() => setStatusFilter(statusFilter === s.key ? "" : s.key)}
                    >
                        <div className="stat-label">{s.label}</div>
                        <div className="stat-value" style={{ color: s.color }}>{s.count}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="filter-row" style={{ marginBottom: 16, flexWrap: "wrap" }}>
                <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="non-reviewed">Not Reviewed</option>
                    <option value="in_progress">Under Review</option>
                    <option value="completed">Reviewed</option>
                </select>
                <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="site">By Site</option>
                    <option value="state">By State</option>
                </select>
                <input type="date" className="filter-select" value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)} />
                <span style={{ color: "var(--text-muted)", alignSelf: "center" }}>to</span>
                <input type="date" className="filter-select" value={toDate}
                    onChange={(e) => setToDate(e.target.value)} />
            </div>

            {reviews.length === 0 ? (
                <div className="empty-state">
                    <h3>No scans found</h3>
                    <p>No scans match the current filters.</p>
                </div>
            ) : (
                <div className="scan-list">
                    {reviews.map((r, i) => (
                        <div
                            key={r.id || `${r.siteId}-${r.scanHour}-${i}`}
                            className="scan-row"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleOpen(r)}
                        >
                            <div className="scan-row-header">
                                <div className="scan-row-main">
                                    <span className="scan-store-name" style={{ fontWeight: 600 }}>
                                        {r.type === "dip" && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                                                background: "rgba(59,130,246,0.15)", color: "#3b82f6", marginRight: 8,
                                            }}>DIP REVIEW</span>
                                        )}
                                        {r.storeName}
                                        <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                                            #{r.siteId}
                                        </span>
                                        {r.stateCode && (
                                            <span style={{
                                                marginLeft: 8, fontSize: 11, fontWeight: 500,
                                                padding: "1px 6px", borderRadius: 8,
                                                background: "var(--bg-secondary)", color: "var(--text-secondary)"
                                            }}>
                                                {r.stateCode}
                                            </span>
                                        )}
                                    </span>
                                    <span style={{ fontSize: 13, color: "var(--text-secondary)", marginLeft: 12 }}>
                                        {(() => {
                                            try {
                                                const d = new Date(r.scanHour.replace(" ", "T") + ":00:00Z");
                                                const tz = r.timezone || "Australia/Melbourne";
                                                return d.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz });
                                            } catch { return r.scanHour; }
                                        })()}
                                    </span>
                                </div>
                                <div className="scan-row-meta">
                                    {r.type === "dip" ? (
                                        r.presetCount > 0 && <span className="scan-presets">{r.presetCount} tank{r.presetCount !== 1 ? "s" : ""}</span>
                                    ) : (
                                        r.presetCount > 0 && <span className="scan-presets">{r.presetCount} presets</span>
                                    )}
                                    {r.avgScore != null && <ScoreBadge score={r.avgScore} />}
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                                        color: statusColor(r.reviewStatus),
                                        background: r.reviewStatus === "completed" ? "rgba(34,197,94,0.1)"
                                            : r.reviewStatus === "in_progress" ? "rgba(59,130,246,0.1)"
                                            : "var(--bg-secondary)"
                                    }}>
                                        {statusLabel(r.reviewStatus)}
                                    </span>
                                    <span style={{ fontSize: 13, color: "var(--text-muted)" }}>›</span>
                                </div>
                            </div>

                            {/* Reviewer info line */}
                            {r.reviewerName && (
                                <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "0 16px 8px" }}>
                                    {r.reviewStatus === "completed" ? "Reviewed" : "Being reviewed"} by {r.reviewerName}
                                    {r.completedAt && (
                                        <span style={{ marginLeft: 8 }}>
                                            {new Date(r.completedAt).toLocaleString("en-AU", { timeZone: r.timezone || "Australia/Melbourne" })}
                                        </span>
                                    )}
                                    {r.issuesRaised > 0 && (
                                        <span style={{ marginLeft: 8, fontWeight: 600, color: "#f97316" }}>
                                            {r.issuesRaised} issue{r.issuesRaised !== 1 ? "s" : ""} raised
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Reviews;
